import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import SidebarLayout from "@/components/SidebarLayout";
import SEO from "@/components/SEO";

const TYPE_ICONS = {
    badge_unlocked: "🏆",
    badge_earned: "🏆",
    campaign_completed: "📬",
    marketplace_purchase: "🛍️",
    plan_renewed: "💳",
    referral_converted: "🎯",
    admin_announcement: "📣",
    data_export_ready: "📦",
    blog_published: "📝",
    // Phase 22b events
    comment_reply: "💬",
    dm_received: "✉️",
    group_join_requested: "👥",
    group_join_approved: "✅",
    group_invite: "📨",
    event_rsvp: "📅",
    event_reminder: "⏰",
    room_role_changed: "🎭",
    system: "⚙️",
};

function formatRelative(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const secs = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
}

export default function Notifications() {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);

    const load = async (newOffset = 0) => {
        setLoading(true);
        try {
            const res = await apiFetch(`/notifications?limit=20&offset=${newOffset}`);
            setItems(newOffset === 0 ? res.items : [...items, ...res.items]);
            setTotal(res.total);
            setOffset(newOffset);
        } catch (e) {
            toast.error(e.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(0); }, []);

    const markAllRead = async () => {
        try {
            // Phase 22b endpoint (kill-switch aware); fallback to legacy Phase 6B.
            try {
                await apiFetch("/notifications/mark-all-seen", { method: "POST" });
            } catch {
                await apiFetch("/notifications/read-all", { method: "POST" });
            }
            setItems((prev) => prev.map((n) => ({ ...n, is_read: true, seen: true })));
            toast.success("All notifications marked as read");
        } catch (e) {
            toast.error(e.message);
        }
    };

    const markRead = async (id) => {
        try {
            try {
                await apiFetch(`/notifications/${id}/mark-seen`, { method: "POST" });
            } catch {
                await apiFetch(`/notifications/${id}/read`, { method: "POST" });
            }
            setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true, seen: true } : n)));
        } catch {}
    };

    const del = async (id) => {
        try {
            await apiFetch(`/notifications/${id}`, { method: "DELETE" });
            setItems((prev) => prev.filter((n) => n.id !== id));
            setTotal((t) => t - 1);
        } catch (e) {
            toast.error(e.message);
        }
    };

    return (
        <SidebarLayout>
            <SEO title="Notifications" description="Your Global Lead Suite notifications" noindex />
            <div className="mx-auto max-w-3xl px-6 py-10" data-testid="notifications-page">
                <div className="flex items-start justify-between mb-8">
                    <div>
                        <p className="text-[11px] uppercase tracking-widest text-text-secondary">Inbox</p>
                        <h1 className="text-3xl font-semibold mt-1 flex items-center gap-2">
                            <Bell className="h-6 w-6" />
                            Notifications
                        </h1>
                        <p className="text-sm text-text-secondary mt-2">{total} total</p>
                    </div>
                    <button
                        onClick={markAllRead}
                        data-testid="notifications-mark-all-read"
                        className="text-xs rounded-full border border-[var(--color-border)] px-4 py-2 hover:border-[var(--color-primary)]/50 transition-colors flex items-center gap-1.5"
                    >
                        <CheckCheck className="h-3.5 w-3.5" />
                        Mark all read
                    </button>
                </div>

                <div className="space-y-2">
                    {items.map((n) => {
                        const isSeen = n.is_read || n.seen;
                        return (
                        <div
                            key={n.id}
                            data-testid={`notification-item-${n.id}`}
                            className={`rounded-xl border p-4 flex gap-3 group transition-colors ${
                                isSeen
                                    ? "border-[var(--color-border)] bg-[var(--color-card)]"
                                    : "border-[var(--color-primary)]/40 bg-[var(--color-primary)]/5"
                            }`}
                        >
                            <div className="text-2xl leading-none pt-1">
                                {TYPE_ICONS[n.type] || TYPE_ICONS[n.event_type] || "🔔"}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <h3 className="text-sm font-medium truncate">{n.title || n.type || n.event_type}</h3>
                                    {!isSeen && (
                                        <span className="h-2 w-2 rounded-full bg-[var(--color-primary)]" />
                                    )}
                                </div>
                                <p className="text-xs text-text-secondary leading-relaxed">{n.body || n.summary}</p>
                                <div className="mt-2 flex items-center gap-3 text-[11px] text-text-secondary">
                                    <span>{formatRelative(n.created_at)}</span>
                                    {n.action_url && (
                                        <button
                                            onClick={() => {
                                                markRead(n.id);
                                                if (n.action_url.startsWith("/")) nav(n.action_url);
                                                else window.location.href = n.action_url;
                                            }}
                                            className="text-[var(--color-secondary)] hover:underline flex items-center gap-1"
                                        >
                                            View <ExternalLink className="h-3 w-3" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={() => del(n.id)}
                                className="text-text-secondary hover:text-[var(--color-alert)] opacity-0 group-hover:opacity-100 transition-opacity"
                                aria-label="Delete"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </button>
                        </div>
                        );
                    })}

                    {!loading && items.length === 0 && (
                        <div
                            data-testid="notifications-empty"
                            className="text-center py-16 text-sm text-text-secondary"
                        >
                            No notifications yet. Kick things off by running a lead search or launching a campaign.
                        </div>
                    )}

                    {loading && (
                        <div className="text-center py-8">
                            <Loader2 className="h-4 w-4 animate-spin inline-block text-text-secondary" />
                        </div>
                    )}

                    {!loading && items.length < total && (
                        <button
                            onClick={() => load(offset + 20)}
                            className="w-full py-3 text-xs text-text-secondary hover:text-text-main"
                        >
                            Load more
                        </button>
                    )}
                </div>
            </div>
        </SidebarLayout>
    );
}
